import React, { useState, useRef, useEffect } from "react";

const COLORS = ["#00ff00", "#ffffff", "#00ffff"];

// Random callsign/type generator (unchanged)
function generateRandomCallsignAndType() {
	const rand = Math.random();

	if (rand < 0.55) {
		const prefixes = ["AAL", "UAL", "DAL", "SWA", "RPA", "FFT", "JBU"];
		const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
		const number = 100 + Math.floor(Math.random() * 900);
		const call = `${prefix}${number}`;

		const jetTypes = ["B738", "B739", "A320", "A321", "B772", "A319"];
		const type = jetTypes[Math.floor(Math.random() * jetTypes.length)];

		return { call, type };
	} else if (rand < 0.75) {
		const prefixes = ["RCH", "VV", "VM", "R", "G", "VIPER", "IGLOO"];
		const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
		const number = 100 + Math.floor(Math.random() * 900);
		const call = `${prefix}${number}`;

		const milTypes = ["C17", "C130", "KC135", "F16", "F18"];
		const type = milTypes[Math.floor(Math.random() * milTypes.length)];

		return { call, type };
	} else {
		const call = `N${Math.floor(100 + Math.random() * 900)}${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${String.fromCharCode(65 + Math.floor(Math.random() * 26))}`;

		const gaTypes = ["C172", "C208", "BE35", "PC12", "C550", "DH8D", "GLF5"];
		const type = gaTypes[Math.floor(Math.random() * gaTypes.length)];

		return { call, type };
	}
}

// NEW: Generate jet (airline) specifically
function generateJetCallsignAndType() {
	const prefixes = ["AAL", "UAL", "DAL", "SWA", "RPA", "FFT", "JBU"];
	const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
	const number = 100 + Math.floor(Math.random() * 900);
	const call = `${prefix}${number}`;

	const jetTypes = ["B738", "B739", "A320", "A321", "B772", "A319"];
	const type = jetTypes[Math.floor(Math.random() * jetTypes.length)];

	return { call, type };
}

// NEW: Generate military specifically
function generateMilitaryCallsignAndType() {
	const prefixes = ["RCH", "VV", "VM", "R", "G", "VIPER", "IGLOO"];
	const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
	const number = 100 + Math.floor(Math.random() * 900);
	const call = `${prefix}${number}`;

	const milTypes = ["C17", "C130", "KC135", "F16", "F18"];
	const type = milTypes[Math.floor(Math.random() * milTypes.length)];

	return { call, type };
}

// NEW: Generate GA/small specifically (for popup)
function generateGACallsignAndType() {
	const call = `N${Math.floor(100 + Math.random() * 900)}${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${String.fromCharCode(65 + Math.floor(Math.random() * 26))}`;

	const gaTypes = ["C172", "C208", "BE35", "PC12", "C550", "DH8D", "GLF5"];
	const type = gaTypes[Math.floor(Math.random() * gaTypes.length)];

	return { call, type };
}

export default function TraconScopeSim() {
	const [targets, setTargets] = useState([]);
	const [ringSpacing, setRingSpacing] = useState(7);
	const [ringOpacity, setRingOpacity] = useState(0.3);
	const [mapScale, setMapScale] = useState(1.0);
	const [draggingId, setDraggingId] = useState(null);
	const [panelOpen, setPanelOpen] = useState(true);

	const [newTargetForm, setNewTargetForm] = useState({
		visible: false,
		x: 0,
		y: 0,
		call: "AAL123",
		type: "B738",
		dest: "DFW",
		alt: "050",
		spd: "210",
		hdg: "090",
	});

	const [editFieldForm, setEditFieldForm] = useState({
		visible: false,
		id: null,
		field: "",
		value: "",
		x: 0,
		y: 0,
	});

	const svgRef = useRef(null);
	const mapScaleRef = useRef(mapScale);
	const mapNaturalSizeRef = useRef({ width: 0, height: 0 });

	useEffect(() => {
		mapScaleRef.current = mapScale;
	}, [mapScale]);

	// Load exact dimensions of tracon.png
	useEffect(() => {
		const img = new Image();
		img.onload = () => {
			mapNaturalSizeRef.current = { width: img.width, height: img.height };
		};
		img.src = "https://www.jordanlynette.com/tracon.png";
	}, []);

	function getPxPerNm(svgW, svgH) {
		const natural = mapNaturalSizeRef.current;
		const currentScale = mapScaleRef.current;

		if (natural.width === 0) return svgW / 50;

		const scaleX = (svgW * currentScale) / natural.width;
		const scaleY = (svgH * currentScale) / natural.height;
		const fitScale = Math.min(scaleX, scaleY);
		const renderedWidth = natural.width * fitScale;

		const rawPxPerNm = renderedWidth / 50;
		return rawPxPerNm * (3 / 5);
	}

	function getMousePosition(evt) {
		const svg = svgRef.current;
		if (!svg) return { x: evt.clientX, y: evt.clientY };
		const rect = svg.getBoundingClientRect();
		return {
			x: evt.clientX - rect.left,
			y: evt.clientY - rect.top,
		};
	}

	function addTarget(e) {
		const ev = e.nativeEvent || e;
		const { x, y } = getMousePosition(ev);

		const random = generateRandomCallsignAndType();

		setNewTargetForm({
			visible: true,
			x,
			y,
			call: random.call,
			type: random.type,
			dest: "DFW",
			alt: "050",
			spd: "210",
			hdg: "090",
		});
	}

	function parseHeadingString(s) {
		if (s == null) return 0;
		const trimmed = String(s).trim();
		const n = parseInt(trimmed, 10);
		if (isNaN(n)) return 0;
		return ((n % 360) + 360) % 360;
	}

	function parseAltitudeString(s) {
		const n = parseInt((s || "0").toString(), 10);
		if (isNaN(n)) return 0;
		return n * 100;
	}

	function confirmAddTarget() {
		const hdgDesired = parseHeadingString(newTargetForm.hdg);
		const spdDesired = Number(newTargetForm.spd) || 0;
		const altDesired = parseAltitudeString(newTargetForm.alt);
		setTargets((prev) => [
			...prev,
			{
				id: Date.now(),
				x: newTargetForm.x,
				y: newTargetForm.y,
				colorIndex: 0,
				call: newTargetForm.call,
				type: newTargetForm.type,
				dest: newTargetForm.dest,
				altDesired,
				altCurr: altDesired,
				spdDesired,
				spdCurr: spdDesired,
				hdgDesired,
				hdgCurr: hdgDesired,
			},
		]);
		setNewTargetForm((s) => ({ ...s, visible: false }));
	}

	function cancelAddTarget() {
		setNewTargetForm((s) => ({ ...s, visible: false }));
	}

	function startDrag(e, id) {
		if (e.button !== 0) return;
		const tag =
			(e.target && e.target.tagName && e.target.tagName.toLowerCase()) || "";
		if (tag === "text" || tag === "tspan" || tag === "input") return;
		setDraggingId(id);
	}

	function stopDrag() {
		setDraggingId(null);
	}

	function onMouseMove(e) {
		if (draggingId === null) return;
		const ev = e.nativeEvent || e;
		const { x, y } = getMousePosition(ev);
		setTargets((prev) =>
			prev.map((t) => (t.id === draggingId ? { ...t, x, y } : t)),
		);
	}

	function openEditFieldForm(e, id, field, currentValue) {
		e.stopPropagation();
		const ev = e.nativeEvent || e;
		const { x, y } = getMousePosition(ev);
		setEditFieldForm({
			visible: true,
			id,
			field,
			value: currentValue != null ? String(currentValue) : "",
			x,
			y,
		});
	}

	function saveEditFieldForm() {
		const { id, field, value } = editFieldForm;
		setTargets((prev) =>
			prev.map((t) => {
				if (t.id !== id) return t;
				if (field === "alt")
					return { ...t, altDesired: parseAltitudeString(value) };
				if (field === "spd") return { ...t, spdDesired: Number(value) || 0 };
				if (field === "hdg")
					return { ...t, hdgDesired: parseHeadingString(value) };
				if (field === "dest") return { ...t, dest: value };
				if (field === "call") return { ...t, call: value };
				if (field === "type") return { ...t, type: value };
				return t;
			}),
		);
		setEditFieldForm({
			visible: false,
			id: null,
			field: "",
			value: "",
			x: 0,
			y: 0,
		});
	}

	function cancelEditFieldForm() {
		setEditFieldForm({
			visible: false,
			id: null,
			field: "",
			value: "",
			x: 0,
			y: 0,
		});
	}

	function cycleColor(e, id) {
		e.preventDefault();
		e.stopPropagation();
		setTargets((prev) =>
			prev.map((t) =>
				t.id === id
					? { ...t, colorIndex: ((t.colorIndex || 0) + 1) % COLORS.length }
					: t,
			),
		);
	}

	// General spawn function
	function spawnTarget({
		distNm,
		angleDeg,
		alt,
		hdg,
		spd,
		dest,
		isJet = false,
		isMilitary = false,
		isGA = false,
	}) {
		const svg = svgRef.current;
		const rect = svg
			? svg.getBoundingClientRect()
			: { width: 800, height: 600 };
		const cx = rect.width / 2;
		const cy = rect.height / 2;
		const pxPerNm = getPxPerNm(rect.width, rect.height);

		const thetaRad = (angleDeg * Math.PI) / 180;
		const x = cx + distNm * pxPerNm * Math.sin(thetaRad);
		const y = cy - distNm * pxPerNm * Math.cos(thetaRad); // y increases down

		let random;
		if (isMilitary) {
			random = generateMilitaryCallsignAndType();
		} else if (isJet) {
			random = generateJetCallsignAndType();
		} else if (isGA) {
			random = generateGACallsignAndType();
		} else {
			random = generateRandomCallsignAndType();
		}

		const hdgDesired = parseHeadingString(hdg);
		const spdDesired = Number(spd) || 0;
		const altDesired = parseAltitudeString(alt);

		setTargets((prev) => [
			...prev,
			{
				id: Date.now(),
				x,
				y,
				colorIndex: 0,
				call: random.call,
				type: random.type,
				dest,
				altDesired,
				altCurr: altDesired,
				spdDesired,
				spdCurr: spdDesired,
				hdgDesired,
				hdgCurr: hdgDesired,
			},
		]);
	}

	// Spawn BARTS (35nm NW)
	function spawnBARTS() {
		spawnTarget({
			distNm: 35,
			angleDeg: 315, // NW
			alt: "150",
			hdg: "140",
			spd: "270",
			dest: "AAC",
			isJet: true,
		});
	}

	// Spawn REELI (35nm NE)
	function spawnREELI() {
		spawnTarget({
			distNm: 35,
			angleDeg: 45, // NE
			alt: "110",
			hdg: "190",
			spd: "270",
			dest: "AAC",
			isJet: true,
		});
	}

	// Spawn KILLR (jet to AAC, 35nm SE)
	function spawnKILLR() {
		spawnTarget({
			distNm: 35,
			angleDeg: 135, // SE
			alt: "100",
			hdg: "010",
			spd: "270",
			dest: "AAC",
			isJet: true,
		});
	}

	// NEW: Spawn KILLR (JKE) (military to JKE, 35nm SE)
	function spawnKILLRJKE() {
		spawnTarget({
			distNm: 35,
			angleDeg: 135, // SE
			alt: "060",
			hdg: "300",
			spd: "270",
			dest: "JKE",
			isMilitary: true,
		});
	}

	// Spawn ATOKA (35nm SW)
	function spawnATOKA() {
		spawnTarget({
			distNm: 35,
			angleDeg: 225, // SW
			alt: "150",
			hdg: "055",
			spd: "270",
			dest: "AAC",
			isJet: true,
		});
	}

	// Spawn popup (small GA aircraft, 7-15nm random)
	function spawnPopup() {
		const distNm = 7 + Math.random() * 8; // 7-15
		const angleDeg = Math.random() * 360;
		const hdg = Math.floor(Math.random() * 360)
			.toString()
			.padStart(3, "0");
		const altHundreds = 19 + Math.floor(Math.random() * 9); // 19-27
		const alt = altHundreds.toString().padStart(3, "0");

		spawnTarget({
			distNm,
			angleDeg,
			alt,
			hdg,
			spd: "210",
			dest: "DFW",
			isGA: true,
		});
	}

	// Movement tick (unchanged)
	useEffect(() => {
		const tickSeconds = 2;
		const iv = setInterval(() => {
			setTargets((prev) => {
				const svg = svgRef.current;
				const rect = svg
					? svg.getBoundingClientRect()
					: { width: 800, height: 600 };
				const pxPerNm = getPxPerNm(rect.width, rect.height);

				const next = [];
				for (const t of prev) {
					if (t.id === draggingId) {
						next.push(t);
						continue;
					}
					let {
						x,
						y,
						hdgCurr = 0,
						hdgDesired = 0,
						spdCurr = 0,
						spdDesired = 0,
						altCurr = 0,
						altDesired = 0,
					} = t;
					hdgCurr = Number(hdgCurr) || Number(hdgDesired) || 0;
					hdgDesired = Number(hdgDesired) || hdgCurr || 0;
					spdCurr = Number(spdCurr) || Number(spdDesired) || 0;
					spdDesired = Number(spdDesired) || spdCurr || 0;
					altCurr = Number(altCurr) || Number(altDesired) || 0;
					altDesired = Number(altDesired) || altCurr || 0;

					let diff = ((((hdgDesired - hdgCurr) % 360) + 540) % 360) - 180;
					const baseTurnDegPerSec = 1.5;
					const speedScale = 1 + spdCurr / 250;
					const maxTurnThisTick = baseTurnDegPerSec * speedScale * tickSeconds;
					let turnAmount = Math.max(
						-maxTurnThisTick,
						Math.min(maxTurnThisTick, diff),
					);
					let newHdg = (hdgCurr + turnAmount + 360) % 360;

					const spdDiff = spdDesired - spdCurr;
					const maxSpdDelta = (5 + (spdCurr / 250) * 5) * (tickSeconds / 2);
					const spdChange = Math.max(
						-maxSpdDelta,
						Math.min(maxSpdDelta, spdDiff),
					);
					const newSpd = spdCurr + spdChange;

					const altDiff = altDesired - altCurr;
					const maxAltDelta = (20 + (spdCurr / 250) * 60) * (tickSeconds / 2);
					const altChange = Math.max(
						-maxAltDelta,
						Math.min(maxAltDelta, altDiff),
					);
					const newAlt = altCurr + altChange;

					const distNm = (newSpd * tickSeconds) / 3600;
					const rad = (newHdg * Math.PI) / 180.0;
					const dxPx = Math.sin(rad) * distNm * pxPerNm;
					const dyPx = -Math.cos(rad) * distNm * pxPerNm;
					const newX = x + dxPx;
					const newY = y + dyPx;

					const margin = 4;
					if (
						newX < -margin ||
						newY < -margin ||
						newX > rect.width + margin ||
						newY > rect.height + margin
					) {
						if (editFieldForm.visible && editFieldForm.id === t.id) {
							setEditFieldForm({
								visible: false,
								id: null,
								field: "",
								value: "",
								x: 0,
								y: 0,
							});
						}
						continue;
					}
					next.push({
						...t,
						x: newX,
						y: newY,
						hdgCurr: newHdg,
						hdgDesired,
						spdCurr: newSpd,
						spdDesired,
						altCurr: newAlt,
						altDesired,
					});
				}
				return next;
			});
		}, 2000);
		return () => clearInterval(iv);
	}, [draggingId]);

	useEffect(() => {
		function onKey(e) {
			if (e.key === "Escape") {
				cancelAddTarget();
				cancelEditFieldForm();
			}
		}
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	function estimateTextSize(text, fontSize = 12) {
		const charWidth = Math.max(6, Math.round(fontSize * 0.6));
		return {
			width: String(text).length * charWidth,
			height: Math.round(fontSize * 1.3),
		};
	}

	function layoutDatablocks(svgWidth, svgHeight, placedTargets) {
		const placedRects = [];
		const result = {};
		for (const t of placedTargets) {
			const line1 = t.call || "";
			const line2 = `${t.dest || ""} / ${t.type || ""}`;
			const line3 = `${displayAlt(t.altCurr || t.altDesired)} ${displaySpd(t.spdCurr || t.spdDesired)}`;
			const size1 = estimateTextSize(line1);
			const size2 = estimateTextSize(line2);
			const size3 = estimateTextSize(line3);
			const width = Math.max(size1.width, size2.width, size3.width) + 8;
			const height = size1.height + size2.height + size3.height + 8;

			const candidates = [
				{ dx: 8, dy: -height + 4 },
				{ dx: 8, dy: 0 },
				{ dx: -width - 8, dy: -height + 4 },
				{ dx: -width - 8, dy: 0 },
				{ dx: 8, dy: -Math.round(height / 2) },
				{ dx: -width - 8, dy: -Math.round(height / 2) },
				{ dx: -Math.round(width / 2), dy: -height - 8 },
				{ dx: -Math.round(width / 2), dy: 8 },
			];

			let chosen = null;
			let minOverlap = Infinity;
			for (const c of candidates) {
				const rx = t.x + c.dx;
				const ry = t.y + c.dy;
				const rect = { x: rx, y: ry, w: width, h: height };
				rect.x = Math.max(2, Math.min(svgWidth - rect.w - 2, rect.x));
				rect.y = Math.max(2, Math.min(svgHeight - rect.h - 2, rect.y));

				let overlapArea = 0;
				for (const pr of placedRects) {
					const ox = Math.max(
						0,
						Math.min(rect.x + rect.w, pr.x + pr.w) - Math.max(rect.x, pr.x),
					);
					const oy = Math.max(
						0,
						Math.min(rect.y + rect.h, pr.y + pr.h) - Math.max(rect.y, pr.y),
					);
					overlapArea += ox * oy;
				}
				if (overlapArea === 0) {
					chosen = rect;
					minOverlap = 0;
					break;
				}
				if (overlapArea < minOverlap) {
					minOverlap = overlapArea;
					chosen = rect;
				}
			}
			placedRects.push(chosen);
			result[t.id] = {
				rect: chosen,
				width,
				height,
				lines: [line1, line2, line3],
			};
		}
		return result;
	}

	function detectConflicts(arr, pxPerNm) {
		const involved = new Set();
		for (let i = 0; i < arr.length; i++) {
			for (let j = i + 1; j < arr.length; j++) {
				const a = arr[i];
				const b = arr[j];
				const dx = a.x - b.x;
				const dy = a.y - b.y;
				const distPx = Math.sqrt(dx * dx + dy * dy);
				const distNm = distPx / (pxPerNm || 1);
				const altA = Number(a.altCurr || a.altDesired || 0);
				const altB = Number(b.altCurr || b.altDesired || 0);
				const altSep = Math.abs(altA - altB);
				if (distNm <= 3 && altSep < 1000) {
					involved.add(a.id);
					involved.add(b.id);
				}
			}
		}
		return involved;
	}

	function displayAlt(altFeet) {
		const hundreds = Math.round((altFeet || 0) / 100);
		return String(hundreds).padStart(3, "0");
	}

	function displaySpd(spd) {
		return String(Math.round(spd || 0)).padStart(3, "0");
	}

	function displayHdg(hdg) {
		return String(Math.round(hdg || 0)).padStart(3, "0");
	}

	const svgRect = svgRef.current
		? svgRef.current.getBoundingClientRect()
		: { width: 800, height: 600 };
	const pxPerNm = getPxPerNm(svgRect.width, svgRect.height);
	const layout = layoutDatablocks(svgRect.width, svgRect.height, targets);

	return (
		<div
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				width: "100vw",
				height: "100vh",
				backgroundColor: "black",
				color: "#00ff00",
				userSelect: "none",
				overflow: "hidden",
				zIndex: 9999,
			}}
			onDoubleClick={addTarget}
		>
			{/* Settings panel — with new spawn buttons */}
			<div style={{ position: "absolute", top: 8, left: 8, zIndex: 50 }}>
				<div
					style={{
						display: panelOpen ? "block" : "none",
						backgroundColor: "rgba(0,0,0,0.7)",
						padding: "8px",
						borderRadius: "4px",
						fontSize: "12px",
					}}
				>
					<div
						style={{
							display: "flex",
							gap: 12,
							alignItems: "center",
							flexWrap: "wrap",
						}}
					>
						<div>
							<div>Range Ring Distance (NM)</div>
							<input
								type="range"
								min="1"
								max="20"
								value={ringSpacing}
								onChange={(e) => setRingSpacing(Number(e.target.value))}
								onDoubleClick={(e) => e.stopPropagation()}
							/>
						</div>
						<div>
							<div>Ring Opacity</div>
							<input
								type="range"
								min="0"
								max="1"
								step="0.05"
								value={ringOpacity}
								onChange={(e) => setRingOpacity(Number(e.target.value))}
								onDoubleClick={(e) => e.stopPropagation()}
							/>
						</div>
						<div>
							<div>Map Scale ({mapScale.toFixed(1)}x)</div>
							<input
								type="range"
								min="0.5"
								max="2.0"
								step="0.05"
								value={mapScale}
								onChange={(e) => setMapScale(Number(e.target.value))}
								onDoubleClick={(e) => e.stopPropagation()}
							/>
						</div>
					</div>
					{/* Spawn buttons */}
					<div
						style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}
					>
						<button
							onClick={spawnBARTS}
							style={{
								backgroundColor: "rgba(0,0,0,0.5)",
								color: "#00ff00",
								border: "1px solid #00ff00",
								padding: "4px 8px",
								borderRadius: "4px",
								cursor: "pointer",
							}}
						>
							Spawn BARTS
						</button>
						<button
							onClick={spawnREELI}
							style={{
								backgroundColor: "rgba(0,0,0,0.5)",
								color: "#00ff00",
								border: "1px solid #00ff00",
								padding: "4px 8px",
								borderRadius: "4px",
								cursor: "pointer",
							}}
						>
							Spawn REELI
						</button>
						<button
							onClick={spawnKILLR}
							style={{
								backgroundColor: "rgba(0,0,0,0.5)",
								color: "#00ff00",
								border: "1px solid #00ff00",
								padding: "4px 8px",
								borderRadius: "4px",
								cursor: "pointer",
							}}
						>
							Spawn KILLR
						</button>
						<button
							onClick={spawnKILLRJKE}
							style={{
								backgroundColor: "rgba(0,0,0,0.5)",
								color: "#00ff00",
								border: "1px solid #00ff00",
								padding: "4px 8px",
								borderRadius: "4px",
								cursor: "pointer",
							}}
						>
							Spawn KILLR (JKE)
						</button>
						<button
							onClick={spawnATOKA}
							style={{
								backgroundColor: "rgba(0,0,0,0.5)",
								color: "#00ff00",
								border: "1px solid #00ff00",
								padding: "4px 8px",
								borderRadius: "4px",
								cursor: "pointer",
							}}
						>
							Spawn ATOKA
						</button>
						<button
							onClick={spawnPopup}
							style={{
								backgroundColor: "rgba(0,0,0,0.5)",
								color: "#00ff00",
								border: "1px solid #00ff00",
								padding: "4px 8px",
								borderRadius: "4px",
								cursor: "pointer",
							}}
						>
							Spawn Popup
						</button>
					</div>
				</div>
				<button
					onClick={() => setPanelOpen((s) => !s)}
					style={{
						marginTop: 6,
						backgroundColor: "rgba(0,0,0,0.5)",
						color: "#00ff00",
						border: "1px solid #00ff00",
						padding: "4px 12px",
						borderRadius: "4px",
						cursor: "pointer",
					}}
				>
					{panelOpen ? "Hide" : "Show"} Settings
				</button>
			</div>

			{/* New target form — always centered */}
			{newTargetForm.visible && (
				<div
					style={{
						position: "absolute",
						left: "50%",
						top: "50%",
						transform: "translate(-50%, -50%)",
						zIndex: 30,
						backgroundColor: "rgba(255,255,255,0.95)",
						color: "black",
						padding: "12px",
						borderRadius: "6px",
						boxShadow: "0 10px 20px -4px rgba(0,0,0,0.3)",
						width: "320px",
					}}
					onClick={(e) => e.stopPropagation()}
					onDoubleClick={(e) => e.stopPropagation()}
				>
					<div style={{ fontSize: 14, fontWeight: "bold", marginBottom: 8 }}>
						New Target (randomized)
					</div>

					<div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
						<input
							value={newTargetForm.call}
							onChange={(e) =>
								setNewTargetForm((s) => ({ ...s, call: e.target.value }))
							}
							placeholder="Callsign"
							style={{ flex: 1 }}
						/>
						<input
							value={newTargetForm.type}
							onChange={(e) =>
								setNewTargetForm((s) => ({ ...s, type: e.target.value }))
							}
							placeholder="Type"
							style={{ flex: 1 }}
						/>
					</div>

					<div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
						<input
							value={newTargetForm.dest}
							onChange={(e) =>
								setNewTargetForm((s) => ({ ...s, dest: e.target.value }))
							}
							placeholder="Dest"
							style={{ flex: 1 }}
						/>
						<input
							value={newTargetForm.alt}
							onChange={(e) =>
								setNewTargetForm((s) => ({ ...s, alt: e.target.value }))
							}
							placeholder="Alt (e.g. 050)"
							style={{ width: 90 }}
						/>
						<input
							value={newTargetForm.spd}
							onChange={(e) =>
								setNewTargetForm((s) => ({ ...s, spd: e.target.value }))
							}
							placeholder="Spd"
							style={{ width: 70 }}
						/>
					</div>

					<div style={{ display: "flex", gap: 8 }}>
						<input
							value={newTargetForm.hdg}
							onChange={(e) =>
								setNewTargetForm((s) => ({ ...s, hdg: e.target.value }))
							}
							placeholder="Hdg (e.g. 090)"
							style={{ width: 90 }}
						/>
					</div>

					<div style={{ display: "flex", gap: 8, marginTop: 12 }}>
						<button
							onClick={confirmAddTarget}
							style={{
								flex: 1,
								padding: "8px",
								background: "#00ff00",
								color: "black",
								border: "none",
								borderRadius: "4px",
								fontWeight: "bold",
							}}
						>
							Add Target
						</button>
						<button
							onClick={cancelAddTarget}
							style={{
								flex: 1,
								padding: "8px",
								background: "#333",
								color: "#fff",
								border: "none",
								borderRadius: "4px",
							}}
						>
							Cancel
						</button>
					</div>
				</div>
			)}

			{/* Edit field form */}
			{editFieldForm.visible && (
				<div
					style={{
						position: "absolute",
						left: editFieldForm.x + 10,
						top: editFieldForm.y + 10,
						zIndex: 40,
						backgroundColor: "rgba(255,255,255,0.95)",
						color: "black",
						padding: "10px",
						borderRadius: "4px",
						boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
					}}
					onClick={(e) => e.stopPropagation()}
					onDoubleClick={(e) => e.stopPropagation()}
				>
					<div style={{ fontSize: 12, fontWeight: "bold" }}>
						Edit {editFieldForm.field}
					</div>
					<input
						value={editFieldForm.value}
						onChange={(e) =>
							setEditFieldForm((s) => ({ ...s, value: e.target.value }))
						}
						style={{ marginTop: 6, width: "100%" }}
					/>
					<div style={{ display: "flex", gap: 6, marginTop: 8 }}>
						<button onClick={saveEditFieldForm}>Save</button>
						<button onClick={cancelEditFieldForm}>Cancel</button>
					</div>
				</div>
			)}

			{/* Fullscreen SVG */}
			<svg
				ref={svgRef}
				style={{ width: "100%", height: "100%", display: "block" }}
				onMouseMove={onMouseMove}
				onMouseUp={stopDrag}
			>
				<image
					href="https://www.jordanlynette.com/tracon.png"
					x={`${(1 - mapScale) * 50}%`}
					y={`${(1 - mapScale) * 50}%`}
					width={`${100 * mapScale}%`}
					height={`${100 * mapScale}%`}
					opacity="0.5"
					preserveAspectRatio="xMidYMid meet"
				/>

				{/* Range rings */}
				{Array.from({ length: 8 }).map((_, i) => (
					<circle
						key={i}
						cx="50%"
						cy="50%"
						r={(i + 1) * ringSpacing * pxPerNm}
						stroke={`rgba(0,255,0,${ringOpacity})`}
						strokeWidth="1"
						fill="none"
					/>
				))}

				{/* Conflict circles */}
				{Array.from(detectConflicts(targets, pxPerNm)).map((id) => {
					const t = targets.find((x) => x.id === id);
					if (!t) return null;
					const radius = 3 * pxPerNm;
					return (
						<circle
							key={`ca-${id}`}
							cx={t.x}
							cy={t.y}
							r={radius}
							stroke="rgba(255,0,0,1)"
							strokeWidth={2}
							fill="none"
						/>
					);
				})}

				{/* Targets + datablocks */}
				{targets.map((t) => {
					const color = COLORS[t.colorIndex] || COLORS[0];
					const hdg = Number(t.hdgCurr || t.hdgDesired || 0);
					const spd = Number(t.spdCurr || t.spdDesired || 0);
					const alt = Number(t.altCurr || t.altDesired || 0);
					const ptlLen = (12 + (spd / 250) * 30) / 2;
					const rad = (hdg * Math.PI) / 180.0;
					const ptx = t.x + Math.sin(rad) * ptlLen;
					const pty = t.y - Math.cos(rad) * ptlLen;
					const itemLayout = layout[t.id] || {
						rect: { x: t.x + 8, y: t.y - 18, w: 80, h: 42 },
						lines: [
							t.call,
							`${t.dest} / ${t.type}`,
							`${displayAlt(alt)} ${displaySpd(spd)}`,
						],
					};
					const { rect, lines } = itemLayout;

					return (
						<g
							key={t.id}
							onMouseDown={(e) => startDrag(e, t.id)}
							onContextMenu={(e) => cycleColor(e, t.id)}
							style={{ cursor: "pointer" }}
						>
							<line
								x1={t.x}
								y1={t.y}
								x2={ptx}
								y2={pty}
								stroke={color}
								strokeWidth={1}
							/>
							<circle cx={t.x} cy={t.y} r="3" fill={color} />

							{detectConflicts(targets, pxPerNm).has(t.id) && (
								<text
									x={rect.x + 6 + lines[0].length * 7}
									y={rect.y + 12}
									fontSize={12}
									fill="red"
									fontFamily="monospace"
								>
									CA
								</text>
							)}

							<rect
								x={rect.x - 4}
								y={rect.y - 4}
								width={rect.w}
								height={rect.h}
								fill="rgba(0,0,0,0.6)"
								rx={4}
							/>

							<text
								x={rect.x}
								y={rect.y + 12}
								fontSize={12}
								fill={color}
								fontFamily="monospace"
								onClick={(e) =>
									openEditFieldForm(
										e,
										t.id,
										"hdg",
										displayHdg(t.hdgDesired || t.hdgCurr),
									)
								}
							>
								{lines[0]}
							</text>
							<text
								x={rect.x}
								y={rect.y + 12 + 14}
								fontSize={12}
								fill={color}
								fontFamily="monospace"
								onClick={(e) => openEditFieldForm(e, t.id, "dest", t.dest)}
							>
								{lines[1]}
							</text>
							<text
								x={rect.x}
								y={rect.y + 12 + 28}
								fontSize={12}
								fill={color}
								fontFamily="monospace"
							>
								<tspan
									onClick={(e) =>
										openEditFieldForm(e, t.id, "alt", displayAlt(alt))
									}
									style={{ cursor: "pointer" }}
								>
									{displayAlt(alt)}
								</tspan>{" "}
								<tspan
									onClick={(e) =>
										openEditFieldForm(e, t.id, "spd", displaySpd(spd))
									}
									style={{ cursor: "pointer" }}
								>
									{displaySpd(spd)}
								</tspan>
							</text>
						</g>
					);
				})}
			</svg>
		</div>
	);
}
